import { supabase } from "@supabaseClient/supabaseClient";

/**
 * Supplier status type
 */
export type SupplierStatus = "active" | "inactive" | "suspended";

/**
 * Supplier type (manufacturer, distributor, wholesaler, etc.)
 */
export type SupplierType =
  | "manufacturer"
  | "distributor"
  | "wholesaler"
  | "retailer"
  | "other"
  | null;

/**
 * Supplier interface matching the database schema
 */
export interface ISupplier {
  id: string;
  organization_id: string;
  external_id?: string | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  status: SupplierStatus;
  supplier_type?: SupplierType;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  contact_person?: string | null;
  payment_terms?: string | null;
  notes?: string | null;
  metadata?: Record<string, string | number | boolean | null | undefined>;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * Supplier with related purchase orders count (for list views)
 */
export interface ISupplierWithOrderCount extends ISupplier {
  purchase_order_count?: number;
  total_spent?: number;
  first_order_date?: string;
  last_order_date?: string;
}

/**
 * Type for Supabase response when querying suppliers with nested purchase orders
 */
interface ISupplierWithPurchaseOrdersResponse extends ISupplier {
  purchase_orders: Array<{
    id: string;
    created_at: string;
    status: string;
    total_amount?: number | null;
  }>;
}

export class SuppliersClient {
  /**
   * Get all suppliers for an organization
   */
  static async getSuppliersByOrganizationId(
    organization_id: string
  ): Promise<ISupplier[]> {
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get a supplier by ID
   */
  static async getSupplierById(supplier_id: string): Promise<ISupplier> {
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("id", supplier_id)
      .is("deleted_at", null)
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Find a supplier by email within an organization
   * Email is normalized to lowercase for matching
   */
  static async getSupplierByEmail(
    email: string,
    organization_id: string
  ): Promise<ISupplier | null> {
    // Normalize email to lowercase for consistent matching
    const normalizedEmail = email.toLowerCase().trim();

    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("organization_id", organization_id)
      .ilike("email", normalizedEmail)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Find a supplier by external_id within an organization
   */
  static async getSupplierByExternalId(
    external_id: string,
    organization_id: string
  ): Promise<ISupplier | null> {
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("organization_id", organization_id)
      .eq("external_id", external_id)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Create a new supplier
   * Email is automatically normalized to lowercase
   */
  static async createSupplier(
    supplier: Omit<ISupplier, "id" | "created_at" | "updated_at" | "deleted_at">
  ): Promise<ISupplier> {
    // Normalize email to lowercase if provided
    const normalizedSupplier = {
      ...supplier,
      email: supplier.email ? supplier.email.toLowerCase().trim() : null,
    };

    const { data, error } = await supabase
      .from("suppliers")
      .insert([normalizedSupplier])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Update a supplier
   * Email is automatically normalized to lowercase if provided
   */
  static async updateSupplier(
    supplier: Omit<ISupplier, "created_at" | "updated_at" | "deleted_at">
  ): Promise<ISupplier> {
    // Normalize email to lowercase if provided
    const normalizedSupplier = {
      ...supplier,
      email: supplier.email ? supplier.email.toLowerCase().trim() : supplier.email,
    };

    const { data, error } = await supabase
      .from("suppliers")
      .update(normalizedSupplier)
      .eq("id", supplier.id)
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Soft delete a supplier (sets deleted_at timestamp)
   */
  static async deleteSupplier(supplier_id: string): Promise<void> {
    const { error } = await supabase
      .from("suppliers")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", supplier_id);

    if (error) {
      throw error;
    }
  }

  /**
   * Find or create a supplier by email
   * Useful when creating purchase orders - automatically links to existing supplier
   */
  static async findOrCreateSupplier(supplierData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    supplier_type?: SupplierType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }): Promise<ISupplier> {
    // First, try to find by email if provided
    if (supplierData.email) {
      const existing = await this.getSupplierByEmail(
        supplierData.email,
        supplierData.organization_id
      );
      if (existing) {
        // Update existing supplier if name or other fields changed
        if (
          existing.name !== supplierData.name ||
          existing.phone !== supplierData.phone ||
          existing.supplier_type !== supplierData.supplier_type
        ) {
          return await this.updateSupplier({
            ...existing,
            name: supplierData.name,
            phone: supplierData.phone ?? existing.phone,
            supplier_type: supplierData.supplier_type ?? existing.supplier_type,
          });
        }
        return existing;
      }
    }

    // Try to find by external_id if provided
    if (supplierData.external_id) {
      const existing = await this.getSupplierByExternalId(
        supplierData.external_id,
        supplierData.organization_id
      );
      if (existing) {
        // Update existing supplier
        return await this.updateSupplier({
          ...existing,
          name: supplierData.name,
          email: supplierData.email ?? existing.email,
          phone: supplierData.phone ?? existing.phone,
          supplier_type: supplierData.supplier_type ?? existing.supplier_type,
        });
      }
    }

    // Create new supplier
    // Normalize email to lowercase if provided
    const normalizedSupplierData = {
      ...supplierData,
      email: supplierData.email ? supplierData.email.toLowerCase().trim() : null,
      status: "active" as SupplierStatus,
    };

    return await this.createSupplier(normalizedSupplierData);
  }

  /**
   * Search suppliers by name or email
   */
  static async searchSuppliers(
    organization_id: string,
    searchTerm: string
  ): Promise<ISupplier[]> {
    const normalizedSearch = searchTerm.toLowerCase().trim();

    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .or(`name.ilike.%${normalizedSearch}%,email.ilike.%${normalizedSearch}%`)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get suppliers with purchase order statistics
   * Useful for supplier list views with analytics
   */
  static async getSuppliersWithOrderStats(
    organization_id: string
  ): Promise<ISupplierWithOrderCount[]> {
    const { data, error } = await supabase
      .from("suppliers")
      .select(
        `
        *,
        purchase_orders:purchase_orders!supplier_id (
          id,
          created_at,
          status,
          total_amount
        )
      `
      )
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    // Transform the data to include order statistics
    return (data || []).map(
      (supplier: ISupplierWithPurchaseOrdersResponse): ISupplierWithOrderCount => {
        const purchaseOrders = supplier.purchase_orders || [];

        const orderDates = purchaseOrders
          .map((po) => new Date(po.created_at))
          .sort((a, b) => a.getTime() - b.getTime());

        const totalSpent = purchaseOrders.reduce((sum, po) => {
          return sum + (po.total_amount ? Number(po.total_amount) : 0);
        }, 0);

        // Destructure to exclude the purchase_orders field
        const { ...supplierWithoutOrders } = supplier;

        return {
          ...supplierWithoutOrders,
          purchase_order_count: purchaseOrders.length,
          total_spent: totalSpent,
          first_order_date:
            orderDates.length > 0 ? orderDates[0].toISOString() : undefined,
          last_order_date:
            orderDates.length > 0
              ? orderDates[orderDates.length - 1].toISOString()
              : undefined,
        };
      }
    );
  }
}
